import type { LucideIcon } from 'lucide-react';
import type { NavigationLayout } from '@cdf/api-client-react';
import { matchPath } from 'react-router-dom';

import { appRouteRegistry, type AppRouteDefinition } from '@/app/route-registry';

export interface NavItem {
  title: string;
  icon: LucideIcon;
  to: string;
}

export interface NavSection {
  heading: string;
  items: NavItem[];
}

export interface NavGroup extends NavSection {
  id: string;
  defaultCollapsed: boolean;
}

export interface GroupedNavigation {
  enabled: boolean;
  groups: NavGroup[];
  unassigned: NavItem[];
  settings?: NavItem;
  pinnedAfterHome: NavItem[];
  pinnedAfterSettings: NavItem[];
  /** Home stays visible before collapsible customer groups. */
  primary?: NavItem;
  flat: NavSection[];
}

/** Shown until customer settings supplies the effective layout. Keep in sync with the API built-in. */
export const BUILT_IN_NAVIGATION_LAYOUT: NavigationLayout = {
  enabled: true,
  groups: [
    { id: 'for-me', label: 'For me', defaultCollapsed: false, items: ['home', 'book', 'my-bookings', 'work-plan', 'my-visitors', 'assistant'] },
    { id: 'my-team', label: 'My team', defaultCollapsed: false, items: ['teams', 'colleagues', 'manager-attendance'] },
    { id: 'workplace', label: 'Workplace', defaultCollapsed: false, items: ['floor-map', 'resources', 'staffed-services'] }
  ]
};

export interface Breadcrumb {
  section: string;
  item: string;
  child?: string;
}

/** Strip trailing splat so nav links target a real URL (`/today/*` → `/today`). */
export function navLinkPath(path: string): string {
  if (path.endsWith('/*')) return path.slice(0, -2) || '/';
  if (path.endsWith('*') && path.length > 1) return path.slice(0, -1) || '/';
  return path;
}

/** Derive sidebar sections from routes that opt into `sidebar` only. */
export function createNavigation(routes: readonly AppRouteDefinition[]): NavSection[] {
  const sections = new Map<string, { order: number; item: NavItem }[]>();

  for (const route of routes) {
    if (!route.sidebar) continue;
    const bucket = sections.get(route.sidebar.section) ?? [];
    bucket.push({
      order: route.sidebar.order,
      item: { title: route.sidebar.label ?? route.title, icon: route.icon, to: navLinkPath(route.path) }
    });
    sections.set(route.sidebar.section, bucket);
  }

  return Array.from(sections, ([heading, items]) => ({
    heading,
    items: items
      .sort((left, right) => left.order - right.order)
      .map((entry) => entry.item)
  }));
}

/** Apply a saved layout only to routes that survived capability and feature filtering. */
export function createGroupedNavigation(
  routes: readonly AppRouteDefinition[],
  layout: NavigationLayout
): GroupedNavigation {
  const flat = createNavigation(routes);
  if (!layout.enabled) return { enabled: false, groups: [], unassigned: [], pinnedAfterHome: [], pinnedAfterSettings: [], flat };

  const sidebarRoutes = routes
    .filter((route) => Boolean(route.sidebar))
    .sort((left, right) => left.sidebar!.order - right.sidebar!.order);
  const items = new Map(sidebarRoutes.map((route) => [route.id, {
    title: route.sidebar?.label ?? route.title,
    icon: route.icon,
    to: navLinkPath(route.path)
  }]));
  const primary = items.get('home');
  items.delete('home');
  const pinnedAfterHome = sidebarRoutes.flatMap((route): NavItem[] => {
    if (!route.sidebar?.pinAfterHome) return [];
    const item = items.get(route.id);
    items.delete(route.id);
    return item ? [item] : [];
  });
  const settings = items.get('settings');
  items.delete('settings');
  const pinnedAfterSettings = sidebarRoutes.flatMap((route): NavItem[] => {
    if (!route.sidebar?.pinAfterSettings) return [];
    const item = items.get(route.id);
    items.delete(route.id);
    return item ? [item] : [];
  });
  const assigned = new Set<string>();
  const groups = layout.groups.flatMap((group): NavGroup[] => {
    const visible = group.items.flatMap((id): NavItem[] => {
      const item = items.get(id);
      if (!item || assigned.has(id)) return [];
      assigned.add(id);
      return [item];
    });
    return visible.length ? [{ id: group.id, heading: group.label, defaultCollapsed: group.defaultCollapsed, items: visible }] : [];
  });
  const unassigned = sidebarRoutes.flatMap((route): NavItem[] => {
    const item = items.get(route.id);
    return item && !assigned.has(route.id) ? [item] : [];
  });
  return { enabled: true, groups, unassigned, settings, pinnedAfterHome, pinnedAfterSettings, primary, flat };
}

/** Derive the top/header menu from routes that opt into `topNav` only. */
export function createTopNavigation(routes: readonly AppRouteDefinition[]): NavItem[] {
  return routes
    .filter((route): route is AppRouteDefinition & { topNav: NonNullable<AppRouteDefinition['topNav']> } =>
      Boolean(route.topNav)
    )
    .sort(
      (left, right) =>
        (left.topNav.order ?? Number.MAX_SAFE_INTEGER) -
        (right.topNav.order ?? Number.MAX_SAFE_INTEGER)
    )
    .map((route) => ({
      title: route.title,
      icon: route.icon,
      to: navLinkPath(route.path)
    }));
}

/** Resolve exact, nested, and parameterized paths from route metadata. */
export function findBreadcrumb(pathname: string): Breadcrumb | undefined {
  const appPath = pathname.replace(/^\/t\/[^/]+/, '') || '/';
  const exactRoute = appRouteRegistry.find((candidate) => candidate.path === appPath);
  const nestedRoute = [...appRouteRegistry]
    .filter((candidate) => candidate.path !== '/')
    .sort((left, right) => right.path.length - left.path.length)
    .find((candidate) => matchPath({ path: candidate.path, end: false }, appPath));
  const route = exactRoute ?? nestedRoute;

  if (!route) return undefined;
  const section = route.breadcrumbSection ?? route.sidebar?.section;
  if (!section) return undefined;
  return { section, item: route.title };
}

// Legacy demo pages remain available as source examples but are not mounted.
// Re-export their icons so those standalone files continue to typecheck.
export {
  ActivityIcon,
  BoxesIcon,
  ChartBarIcon,
  FileIcon,
  FileTextIcon,
  LayoutDashboardIcon,
  PackageIcon,
  SettingsIcon,
  ShieldIcon,
  SparklesIcon,
  UploadIcon
} from 'lucide-react';
